<template>
  <div class="w-full h-full flex flex-col">
    <!-- Chart -->
    <div class="flex-1">
      <canvas ref="chartRef"></canvas>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import Chart from 'chart.js/auto';

export default {
  name: 'KtbYieldBandChart',
  props: {
    transactions: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const chartRef = ref(null);
    let chart = null;

    const processData = transactions => {
      if (!transactions || transactions.length === 0) {
        // 데이터가 없을 때 기본 데이터 반환
        const currentTime = new Date();
        const times = [];
        const spreads = [];

        // 현재 시각 기준으로 1분 간격으로 30개의 빈 데이터 포인트 생성
        for (let i = 0; i < 30; i++) {
          const time = new Date(currentTime - i * 60000);
          times.unshift(time.toTimeString().substring(0, 5));
          spreads.unshift(null);
        }

        return {
          times,
          spreads,
          spreadRange: 10, // 기본 y축 범위 ±10bp
        };
      }

      // 기존 데이터 처리 로직
      const sortedTransactions = [...transactions].sort((a, b) =>
        a.time.localeCompare(b.time)
      );

      const times = sortedTransactions.map(tx => tx.time.substring(0, 5));

      const spreads = sortedTransactions.map(tx => parseFloat(tx.spreadBp));

      const maxSpread = Math.max(...spreads);
      const minSpread = Math.min(...spreads);
      const spreadRange = Math.max(Math.abs(maxSpread), Math.abs(minSpread));

      return {
        times,
        spreads,
        spreadRange,
      };
    };

    const createChart = data => {
      const ctx = chartRef.value.getContext('2d');

      if (chart) {
        chart.destroy();
      }

      chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.times,
          datasets: [
            {
              label: '스프레드',
              data: data.spreads,
              borderColor: context => {
                const value = context.raw;
                if (value === null) return 'rgba(200, 200, 200, 0.5)'; // 데이터 없을 때 회색으로 표시
                return value > 0 ? 'rgb(59, 130, 246)' : 'rgb(239, 68, 68)';
              },
              borderWidth: 2,
              radius: 0,
              tension: 0.1,
              fill: false,
              spanGaps: true,
              segment: {
                borderColor: context => {
                  const value1 = context.p0.parsed.y;
                  const value2 = context.p1.parsed.y;
                  if (value1 === null || value2 === null)
                    return 'rgba(200, 200, 200, 0.5)';
                  if (value1 > 0 && value2 > 0) return 'rgb(59, 130, 246)';
                  if (value1 < 0 && value2 < 0) return 'rgb(239, 68, 68)';
                  return createGradient(ctx, value1, value2);
                },
              },
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: 'index',
          },
          scales: {
            y: {
              title: {
                display: true,
                text: '스프레드 (bp)',
              },
              ticks: {
                callback: function (value) {
                  return value.toFixed(1);
                },
              },
              grid: {
                display: true,
                drawOnChartArea: true,
                color: context => {
                  if (context.tick.value === 0) {
                    return 'rgba(0, 0, 0, 0.2)';
                  }
                  return 'rgba(0, 0, 0, 0.1)';
                },
              },
              // y축 범위를 0을 중심으로 대칭되게 설정
              min: -data.spreadRange,
              max: data.spreadRange,
              beginAtZero: false,
            },
            x: {
              title: {
                display: true,
              },
              grid: {
                display: false,
              },
              ticks: {
                maxRotation: 30,
                minRotation: 30,
                autoSkip: true,
                maxTicksLimit: 12,
              },
            },
          },
          plugins: {
            legend: {
              position: 'top',
              labels: {
                padding: 1,
              },
            },
            tooltip: {
              displayColors: false,
              padding: 10,
              callbacks: {
                label: function (context) {
                  return `스프레드: ${context.raw.toFixed(1)}bp`;
                },
              },
            },
          },
        },
      });
    };

    // 0을 교차하는 선에 대한 그라데이션 생성 함수
    const createGradient = ctx => {
      const gradient = ctx.createLinearGradient(0, 0, 0, 300);
      gradient.addColorStop(0, 'rgb(255, 99, 132)'); // 빨간색 (양수)
      gradient.addColorStop(1, 'rgb(75, 192, 192)'); // 청록색 (음수)
      return gradient;
    };

    watch(
      () => props.transactions,
      newTransactions => {
        const data = processData(newTransactions);
        createChart(data);
      },
      { deep: true }
    );

    onMounted(() => {
      const data = processData(props.transactions);
      createChart(data);
    });

    return {
      chartRef,
    };
  },
};
</script>
